import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { CandidateFilledIcon } from '@modernloop/shared/icons';
import { Box, Stack, Typography } from '@mui/material';

import {
  ResponseStatus,
  ScheduleContentInterviewDetails_CandidateEventFragment,
  ScheduleContentInterviewDetails_InterviewFragment,
} from 'src/generated/mloop-graphql';

import AdditionalAttendeesDetails from 'src/entities/common/AdditionalAttendeesDetails';
import getResponseStatusNode from 'src/entities/common/getResponseStatusNode';

import { useOrgDirectoryService } from 'src/hooks/directoryService';

import ScheduleContentInterviewDetailsInterviewer from './ScheduleContentInterviewDetailsInterviewer';
import ScheduleContentInterviewDetailsMeetingRoom from './ScheduleContentInterviewDetailsMeetingRoom';

type Fragments = {
  interview: ScheduleContentInterviewDetails_InterviewFragment;
  candidateEvent: ScheduleContentInterviewDetails_CandidateEventFragment | undefined;
};

type Props = {
  timezone: string;
};

const ScheduleContentInterviewDetails: FCWithFragments<Fragments, Props> = ({
  interview,
  candidateEvent,
  timezone,
}) => {
  const { t } = useTranslation();
  const directoryService = useOrgDirectoryService();

  return (
    <Stack spacing={1} data-testid="schedule-content-interview-details-" sx={{ pl: 2.5, pr: 1.5, py: 1 }}>
      {candidateEvent && !interview.isHiddenFromCandidate && (
        <Stack
          direction="row"
          justifyContent="space-between"
          data-testid="schedule-content-interview-details-candidate-rsvp"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CandidateFilledIcon color="action" />
            <Typography variant="body2">{t('hiring-dashboard-common:common.hiringProcess.candidateColumn')}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {getResponseStatusNode(candidateEvent.responseStatus || ResponseStatus.NeedsAction, t)}

            {/* We use this box to align the candidate RSVP with that of interviewer RSVP */}
            {directoryService?.canChangeRsvp() && <Box width={32} height={32} />}
          </Stack>
        </Stack>
      )}

      {interview.applicationStageInterviewInterviewers?.map((interviewer) => (
        <ScheduleContentInterviewDetailsInterviewer
          key={`${interview.id}-${interviewer.interviewerId}`}
          interview={interview}
          interviewer={interviewer}
          timezone={timezone}
        />
      ))}

      {interview.applicationStageInterviewRooms?.map((room) => (
        <ScheduleContentInterviewDetailsMeetingRoom key={room.applicationStageInterviewId} meetingRoom={room} />
      ))}

      <AdditionalAttendeesDetails additionalAttendees={interview.additionalAttendeesV2} />
    </Stack>
  );
};

ScheduleContentInterviewDetails.fragments = {
  interview: gql`
    ${ScheduleContentInterviewDetailsInterviewer.fragments.interview}
    ${ScheduleContentInterviewDetailsInterviewer.fragments.interviewer}
    ${ScheduleContentInterviewDetailsMeetingRoom.fragments.meetingRoom}
    ${AdditionalAttendeesDetails.fragments.additionalAttendees}
    fragment ScheduleContentInterviewDetails_interview on ApplicationStageInterview {
      id
      additionalAttendeesV2 {
        ...AdditionalAttendeesDetails_additionalAttendees
      }
      ...ScheduleContentInterviewDetailsInterviewer_interview
      applicationStageInterviewInterviewers {
        interviewerId
        ...ScheduleContentInterviewDetailsInterviewer_interviewer
      }
      applicationStageInterviewRooms {
        applicationStageInterviewId
        meetingRoomId
        ...ScheduleContentInterviewDetailsMeetingRoom_meetingRoom
      }
    }
  `,
  candidateEvent: gql`
    fragment ScheduleContentInterviewDetails_candidateEvent on ApplicationStageCandidateEvent {
      id
      responseStatus
    }
  `,
};

export default ScheduleContentInterviewDetails;
